import { find, get, isArray, isEmpty, split } from 'lodash';

import { IAppState } from '@store/index';
import { ICompany, ENTITY_TYPES } from '@shared/types';
import { ACCESS_TOKEN, ENTITY_ID_PATH, PERSONAL_TOKEN } from '@config/index';

const INVENTORY_PREFERENCES_PATH = [
  'preferences',
  'company',
  'back_office',
  'inventory',
];
const LOCALES = ['userSession', 'i18n', 'locales'];

export const STATE_KEYS = {
  ACCESS_TOKEN: ['userSession', ACCESS_TOKEN],
  PERSONAL_TOKEN: ['userSession', PERSONAL_TOKEN],
  CURRENT_COMPANY_ID: ['userSession', 'currentCompanyId'],
  APPLICATION_STARTED: ['userSession', 'applicationStarted'],
  COMPANY_ROLES: ['userSession', 'companyRoles'],
  USER_COMPANIES: ['userSession', 'companies'],
  ROUTER_HISTORY: ['tools', 'history'],
  IFRAME_MODE: ['userSession', 'isIframeMode'],
  IFRAME_POST_MESSAGE_RESULT: ['tools', 'iframePostMessageResult'],
  FORM_MODE: ['ui', 'formMode'],
  UI_LIST: ['ui', 'list'],
  UI_LIST_DEFAULT_VIEWS: ['ui', 'list', 'defaultViews'],
  UI_DELETE_VIEW_MODE: ['ui', 'isDeleteViewMode'],
  UI_EDITOR: ['ui', 'editor'],
  UI_EDITOR_ENTITY_TYPE: ['ui', 'editor', 'entity_type'],
  UI_EDITOR_FORM_VALUE: ['ui', 'editor', 'formValue'],
  UI_EDITOR_FORM_VALUE_NAME: ['ui', 'editor', 'formValue', 'name'],
  UI_EDITOR_FORM_VALUE_DEVICE_NAME: [
    'ui',
    'editor',
    'formValue',
    'device',
    'name',
  ],
  UI_EDITOR_FORM_VALUE_ID: ['ui', 'editor', 'formValue', 'id'],
  UI_EDITOR_DOCUMENT_IDS: ['ui', 'editor', 'documentIds'],
  UI_EDITOR_OPTIONS_STATE: ['ui', 'editor', 'options', 'state'],
  UI_EDITOR_OPTIONS_DUPLICATE: ['ui', 'editor', 'options', 'duplicateOption'],
  UI_LIST_ENTITY_TYPE: ['ui', 'list', 'entityType'],
  UI_DOCUMENT_CHANGES: ['ui', 'documentValueChanges'],
  UI_GRID_ROW_LOADER: ['ui', 'gridRowLoader'],
  USER_PERMISSIONS: ['userSession', 'userPermissions', 'items'],
  SIDEBAR_ITEMS: ['ui', 'menuItems'],
  STORES_SECTION: ['stores'],
  STORES_LIST: ['stores', 'list'],
  IS_STORES_LIST_LOAD: ['stores', 'isListLoaded'],
  STORES_TOTAL: ['stores', 'total'],
  SELECTED_STORES: ['stores', 'selectedStores'],
  SITE_GROUPS: ['stores', 'siteGroups'],
  FEATURES: ['tools', 'features'],
  API_INFO: ['tools', 'apiInfo'],
  FORM_MODAL: ['ui', 'formModal'],
  FORM_MODAL_RESULT: ['ui', 'formModalResult'],
  USER_CONFIG: ['ui', 'userConfig'],
  COMPANY_PREFERENCES: ['preferences', 'company'],
  COMPANY_PREFERRED_FEATURES: ['preferences', 'company', 'features'],
  CURRENT_ENTITY_TYPE: ['tools', 'currentEntityType'],
  CURRENT_USER_NAME: ['userSession', 'user', 'name'],
  CURRENT_USER_ID: ['userSession', 'user', 'id'],
  POLLER_STATES: ['poller'],
  OPERATION_RESULT: ['tools', 'operationResult'],
  ACTION_STATUS: ['tools', 'actionStatuses'],
  NOTIFICATION_BOX: ['ui', 'notificationBox'],
  SAVE_STATUS_STATE: ['ui', 'saveStatusState'],
  SELECT_EVENTS: ['events', 'selectedEvents'],
  EVENTS: ['events', 'list'],
  SELECT_EVENT_IDS: ['events', 'selectedEventIds'],
  XDM_CURRENCY_PRECISION: [
    'preferences',
    'company',
    'regional_settings',
    'default_currency_scheme_entity',
    'precision',
  ],
  XVI_NUMBER_PRECISION: [...INVENTORY_PREFERENCES_PATH, 'quantity_precision'],
  XDM_CURRENCY_CODE: [
    'preferences',
    'company',
    'regional_settings',
    'default_currency_scheme_entity',
    'code',
  ],
  XDM_CURRENCY_SYMBOL: [
    'preferences',
    'company',
    'regional_settings',
    'default_currency_scheme_entity',
    'symbol',
  ],
  XDM_THOUSANDS_SEPARATOR: [
    'preferences',
    'company',
    'regional_settings',
    'thousands_separator',
  ],
  XDM_DECIMAL_SEPARATOR: [
    'preferences',
    'company',
    'regional_settings',
    'decimal_separator',
  ],
  XDM_NEGATIVES: ['preferences', 'company', 'regional_settings', 'negatives'],
  XVI_CURRENCY_PRECISION: [
    ...INVENTORY_PREFERENCES_PATH,
    'calculation_precision',
  ],
  TRANSFER_ITEM_LIST_VIEW: [
    ...INVENTORY_PREFERENCES_PATH,
    'transfer_item_list_view_setting',
  ],
  TRANSFER_DISPLAY_QOH_FROM: [
    ...INVENTORY_PREFERENCES_PATH,
    'transfer_display_qoh_from_setting',
  ],
  ORDER_ITEM_LIST_VIEW: [
    ...INVENTORY_PREFERENCES_PATH,
    'inventory_order_item_list_view_setting',
  ],
  XDM_STAND_WORKSHEET_PHYSICAL_TERMINAL: [
    ...INVENTORY_PREFERENCES_PATH,
    'physical_terminal',
  ],
  XDM_STAND_WORKSHEET_CASH_TERMINAL: [
    ...INVENTORY_PREFERENCES_PATH,
    'cash_terminal',
  ],
  XDM_STAND_WORKSHEET_RECEIPT_PHYSICAL: [
    ...INVENTORY_PREFERENCES_PATH,
    'receipt_physical',
  ],
  XDM_STAND_WORKSHEET_ENTRY_FORM_SUMMARY_VIEW: [
    ...INVENTORY_PREFERENCES_PATH,
    'entry_form_view_setting',
  ],
  XDM_STAND_WORKSHEET_GLOBAL_PRICE_SETTING: [
    ...INVENTORY_PREFERENCES_PATH,
    'global_price_setting',
  ],
  XDM_DURING_TRANSFER_AUTO_COMPLETE_SETTING: [
    ...INVENTORY_PREFERENCES_PATH,
    'transfer_during_event_transfer_setting',
  ],
  XDM_STAND_WORKSHEET_DEPARTMENT_CODE_SETTING: [...INVENTORY_PREFERENCES_PATH],
  XDM_STAND_WORKSHEET_POST_CLOSURE_SETTING: [
    ...INVENTORY_PREFERENCES_PATH,
    'stand_worksheet_post_closure_setting',
  ],
  INVENTORY_COUNT_LIST_VIEW: [
    ...INVENTORY_PREFERENCES_PATH,
    'inventory_count_sheet_view_setting',
  ],
  EVENT_AUTO_POPULATE_TRANSFER_EVENT: [
    'events',
    'newTransferAutoPopulateEvent',
  ],
  PENDING_REQUEST_ALERT: [
    ...INVENTORY_PREFERENCES_PATH,
    'pending_request_alert',
  ],
  PENDING_REQUEST_TIMEOUT: [
    ...INVENTORY_PREFERENCES_PATH,
    'pending_request_alert_color_change_timeout',
  ],
  LOCALES: [...LOCALES],
  SELECTED_USER_LOCALE: [...LOCALES, 'userLocale'],

  XDM_CROWDIN_CUSTOM: ['preferences', 'company', 'crowdin_custom_language'],
};

export const companySelectorFn = (state: IAppState): ICompany => {
  const copmanyId = get(state, STATE_KEYS.CURRENT_COMPANY_ID);

  return find<ICompany>(get(state, STATE_KEYS.USER_COMPANIES, []), {
    id: copmanyId,
  } as Partial<ICompany>);
};

export const companySubscriptionsSelectorFn = (state: IAppState): string[] => {
  return get<ICompany, any, string[]>(
    companySelectorFn(state),
    'subscriptions',
    [],
  );
};

export const entitySchemaSelector = (
  entityType: string,
): ((state: IAppState) => object) => {
  return (state: IAppState): object => {
    return get(state, ['entities', entityType, 'schema']);
  };
};

export const entityItemsSelector = (entityType: string): string[] => {
  return ['entities', entityType, 'items'];
};

export const schemaNodeSelector = (
  entityType: string,
  path?: string | string[],
): ((state: IAppState) => object) => {
  return (state: IAppState): object => {
    const schema = entitySchemaSelector(entityType)(state);

    if (!path || isEmpty(path)) {
      return schema;
    }

    const propertyPath = isArray(path) ? path : split(path, '.');
    const schemaPath = propertyPath
      .map((item) => `properties.${item}`)
      .join('.');

    return get(schema, schemaPath);
  };
};

export const bufferedItemsByEntitySelector = (
  entityType: ENTITY_TYPES,
): string[] => {
  return ['ui', 'bufferedDisplayedDocuments', entityType, ENTITY_ID_PATH];
};

export const predefinedFilterModelSelector = (state: IAppState): object => {
  return get(state, [...STATE_KEYS.UI_LIST, 'predefinedFilterModel']);
};

export const STATE_SELECTORS = {
  entitySchemaSelector,
  entityItemsSelector,
  schemaNodeSelector,
  bufferedItemsByEntitySelector,
  predefinedFilterModelSelector,
  CURRENT_COMPANY: companySelectorFn,
  COMPANY_SUBSCRIPTIONS: companySubscriptionsSelectorFn,
  ...STATE_KEYS,
};
